import type { CraftRequest, CraftResponse } from '@/types/domain';

export interface CraftApiResult {
  response: CraftResponse;
  model: string;
}

export interface ServerConfig {
  model: string;
  mock: boolean;
  hasKey: boolean;
}

export class CraftError extends Error {}

let activeController: AbortController | null = null;

export async function craft(req: CraftRequest, signal?: AbortSignal): Promise<CraftApiResult> {
  if (activeController) activeController.abort();
  activeController = new AbortController();
  const combined = signal
    ? AbortSignal.any([activeController.signal, signal])
    : activeController.signal;

  let res: Response;
  try {
    res = await fetch(`${import.meta.env.BASE_URL}api/craft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
      signal: combined,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw new CraftError('Cancelled');
    throw new CraftError('Network error reaching the crafting server.');
  } finally {
    if (activeController?.signal === combined) activeController = null;
  }

  if (!res.ok) {
    let message = 'AI generation failed';
    try {
      const j = (await res.json()) as { message?: string; error?: string };
      message = j.message || j.error || message;
    } catch {
      /* ignore */
    }
    throw new CraftError(message);
  }
  return (await res.json()) as CraftApiResult;
}

export async function fetchConfig(): Promise<ServerConfig> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}api/config`);
    if (res.ok) return (await res.json()) as ServerConfig;
  } catch {
    /* ignore */
  }
  return { model: 'openai/gpt-4o-mini', mock: true, hasKey: false };
}

export async function fetchHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}api/health`);
    return res.ok;
  } catch {
    return false;
  }
}
